// Settings → Notifications tab.
//
// Per-user opt-in toggles for each notification type. Backed by:
//   GET  /api/portal/notification-preferences  → { preferences: {type: bool} }
//   PUT  /api/portal/notification-preferences  → save toggles
//
// Per-user, not per-retailer — RLS on portal_notification_preferences
// scopes by app.user_id. Default-on for every type (missing row = on).

import { NOTIFICATION_TYPES, NOTIFICATION_LABELS, iconMarkup, type NotificationType } from "@tkos/shared-ui";

import type { PageContext } from "../../../shell/types";
import type { SettingsTab, TabCleanup } from "../types";

import "./notifications.css";

interface PrefsResponse {
  ok?: boolean;
  preferences?: Partial<Record<NotificationType, boolean>>;
}

/** Short hint copy explaining when each type fires. */
const HINT: Readonly<Record<NotificationType, string>> = {
  submission_received:     "When a consumer wizard or website form submits a new lead.",
  message_received:        "When a customer, supplier, or team member messages you in the portal.",
  quote_viewed:            "When a customer opens a quote you sent.",
  quote_signed:            "When a customer e-signs a quote.",
  payment_received:        "When a customer payment lands.",
  delivery_status_changed: "When a delivery moves between dispatched, en route, or delivered.",
  booking_requested:       "When a customer requests an appointment.",
  snag_raised:             "When a snag is logged against a project.",
  supplier_deal_posted:    "When a supplier posts a new offer you can use.",
  lead_scored_hot:         "When the lead-scoring engine flags a lead as hot.",
  layout_needs_review:     "When a customer's AI kitchen layout couldn't be finished automatically and needs a manual check.",
};

const notificationsTab: SettingsTab = {
  id: "notifications",
  label: "Notifications",
  icon: iconMarkup("bell", { size: "sm" }),
  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    let aborted = false;
    const root = document.createElement("div");
    root.className = "set-notif-root";

    const intro = document.createElement("p");
    intro.className = "set-notif-intro";
    intro.textContent =
      "Choose which events trigger a slide-in notification and add to your bell badge. " +
      "These settings are per-user — your team members each control their own.";
    root.appendChild(intro);

    const card = document.createElement("div");
    card.className = "set-notif-card";

    const status = document.createElement("div");
    status.className = "set-notif-status";
    status.textContent = "Loading…";

    const footer = document.createElement("div");
    footer.className = "set-notif-footer";
    footer.appendChild(status);
    root.appendChild(card);
    root.appendChild(footer);

    container.replaceChildren(root);

    // ── Build a row per known type (initially disabled, populated on load).
    const inputs = new Map<NotificationType, HTMLInputElement>();
    for (const type of NOTIFICATION_TYPES) {
      const row = document.createElement("div");
      row.className = "set-notif-row";

      const label = document.createElement("label");
      label.className = "set-notif-row__label";
      const title = document.createElement("span");
      title.className = "set-notif-row__title";
      title.textContent = NOTIFICATION_LABELS[type];
      const hint = document.createElement("span");
      hint.className = "set-notif-row__hint";
      hint.textContent = HINT[type];
      label.appendChild(title);
      label.appendChild(hint);
      row.appendChild(label);

      const sw = document.createElement("label");
      sw.className = "set-notif-switch";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.checked = true;
      input.disabled = true;
      input.setAttribute("aria-label", NOTIFICATION_LABELS[type]);
      const slider = document.createElement("span");
      slider.className = "set-notif-switch__slider";
      sw.appendChild(input);
      sw.appendChild(slider);
      row.appendChild(sw);

      card.appendChild(row);
      inputs.set(type, input);
    }

    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    let saving = false;

    const showStatus = (msg: string, tone: "" | "saved" | "error" = ""): void => {
      status.textContent = msg;
      if (tone) status.dataset["tone"] = tone;
      else delete status.dataset["tone"];
    };

    const persist = async (): Promise<void> => {
      if (saving) return;
      saving = true;
      const preferences: Partial<Record<NotificationType, boolean>> = {};
      for (const [type, input] of inputs) preferences[type] = input.checked;
      showStatus("Saving…");
      try {
        await ctx.api<PrefsResponse>("/portal/notification-preferences", {
          method: "PUT",
          body: JSON.stringify({ preferences }),
        });
        showStatus("Saved.", "saved");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Save failed";
        showStatus(msg, "error");
      } finally {
        saving = false;
      }
    };

    const onToggle = (): void => {
      if (saveTimer !== null) clearTimeout(saveTimer);
      saveTimer = setTimeout(() => {
        saveTimer = null;
        void persist();
      }, 400);
    };

    // Wire change listeners.
    for (const input of inputs.values()) {
      input.addEventListener("change", onToggle);
    }

    // ── Initial load ─────────────────────────────────────────────────────
    void (async (): Promise<void> => {
      try {
        const resp = await ctx.api<PrefsResponse>("/portal/notification-preferences");
        if (aborted) return;
        const prefs = (resp && resp.preferences) || {};
        for (const [type, input] of inputs) {
          input.checked = prefs[type] !== false; // default-on
          input.disabled = false;
        }
        showStatus("Per-user preferences. Changes save automatically.");
      } catch (err) {
        if (aborted) return;
        // Backend not reachable — leave defaults enabled but writable so
        // the user can still try; persist will report the error.
        for (const input of inputs.values()) input.disabled = false;
        const msg = err instanceof Error ? err.message : "Could not load preferences";
        showStatus(msg, "error");
      }
    })();

    return () => {
      aborted = true;
      if (saveTimer !== null) clearTimeout(saveTimer);
      for (const input of inputs.values()) {
        input.removeEventListener("change", onToggle);
      }
    };
  },
};

export default notificationsTab;
